// ── safeFetch — fetch + Zod validation in one step ───────────────────────────
// Standard pattern for all API calls in the monorepo.
// Uses .safeParse() internally — never throws on validation failure.

import type { z } from 'zod'

import { ProblemDetailsWireSchema } from '@platform/errorzen-contracts'

// ── Result types ─────────────────────────────────────────────────────────────

export type SafeFetchSuccess<T> = {
  ok: true
  data: T
  status: number
}

export type SafeFetchError = {
  ok: false
  error: Error
  status?: number
}

export type SafeFetchResult<T> = SafeFetchSuccess<T> | SafeFetchError

// ── Error classes ────────────────────────────────────────────────────────────

export class FetchValidationError extends Error {
  constructor(
    message: string,
    public readonly issues: z.ZodIssue[],
    public readonly status: number,
  ) {
    super(message)
    this.name = 'FetchValidationError'
  }
}

/**
 * Typed error for a non-2xx `safeFetch` response.
 *
 * Backends across the monorepo return error bodies in several different
 * shapes (see `extractErrorShape` below for the full normalization table).
 * `ApiError` tolerantly parses whichever shape shows up and exposes it
 * through a single, stable interface so callers never have to re-implement
 * shape-sniffing at every call site.
 *
 * - `code` / `details` / `traceId` / `requestId` / `retryable` are `null`
 *   when the backend didn't provide them — never `undefined`, so callers can
 *   rely on `=== null` checks.
 * - `body` is the raw parsed response body (or raw text if it wasn't valid
 *   JSON) for callers that need to inspect fields this normalizer doesn't
 *   surface.
 * - v2 (ASC-6338): `title` / `retryAfter` / `problemType` populate from a
 *   recognized RFC 9457 `application/problem+json` body (see the problem+json
 *   branch in `buildApiError`); `retryAfter` also falls back to the
 *   `Retry-After` response header on every path. All three are `null` when
 *   not provided.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string | null,
    public readonly details: unknown,
    public readonly traceId: string | null,
    public readonly requestId: string | null,
    public readonly retryable: boolean | null,
    public readonly body: unknown,
    public readonly title: string | null = null,
    public readonly retryAfter: number | null = null,
    public readonly problemType: string | null = null,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Type guard — narrows `unknown`/`Error` to `ApiError`. */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

/**
 * Type guard — narrows `unknown`/`Error` to `FetchValidationError`.
 *
 * The sibling of `isApiError`, and it exists for the same reason: consumers
 * must not write `err instanceof FetchValidationError` at the call site.
 * `error-handling.md` lists `instanceof` on these classes across a package
 * boundary as a forbidden pattern, so the check belongs here, in the package
 * that owns the class. Ref: ASC-8061.
 */
export function isFetchValidationError(error: unknown): error is FetchValidationError {
  return error instanceof FetchValidationError
}

// ── Error-envelope normalization ─────────────────────────────────────────────
// Backends currently in the wild return non-2xx bodies in these shapes.
// `extractErrorShape` tolerantly extracts whichever fields are present —
// it never throws, and unrecognized shapes fall through to `emptyShape()`.
//
// | # | Shape                                                              | Source style                    |
// |---|---------------------------------------------------------------------|----------------------------------|
// | 1 | `{ detail: "string" }`                                             | FastAPI default                  |
// | 2 | `{ detail: { code, message\|error, request_id?, trace_id?, retryable? } }` | llmrouterzen / contextzen / DI  |
// | 3 | `{ code, message }` (flat)                                         | flat backend style                |
// | 4 | `{ error: "string", code?, details? }`                            | insights / developer_api          |
// | 5 | `{ error: { code, message, ... } }` (nested)                       | future errorzen envelope          |
// | 6 | `{ detail: [{ loc, msg, type }, ...] }`                            | FastAPI 422 validation error      |
// | 7 | non-JSON / empty body                                              | proxy / gateway error pages       |
//
// `requestId`/`traceId` also fall back to `X-Request-Id`/`X-Trace-Id` response
// headers when absent from the body. Message precedence: backend-provided
// message string, else the legacy `HTTP ${status}: ${statusText}` format —
// this keeps existing string-matching consumers working unchanged.

type ExtractedShape = {
  message: string | null
  code: string | null
  details: unknown
  traceId: string | null
  requestId: string | null
  retryable: boolean | null
}

function emptyShape(): ExtractedShape {
  return {
    message: null,
    code: null,
    details: null,
    traceId: null,
    requestId: null,
    retryable: null,
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Strips Pydantic's `model_validator` prefix, which is noise to whoever reads the message. */
function stripValueErrorPrefix(msg: string): string {
  return msg.replace(/^Value error,\s*/, '')
}

/**
 * Pulls the real reason out of an errorzen problem+json `details.validation` array.
 *
 * THIS is the shape Scorpio services actually return, and it is NOT the FastAPI array handled by
 * `validationMessage` below. A problem body carries a STRING `detail` — for a 422 that string is
 * the generic registry copy, "The request failed validation." — while the specific, actionable
 * message sits in `details.validation[].message`, which nothing rendered. So an operator saving a
 * connector saw the generic title and had to open devtools to find out which field was wrong.
 *
 * `pointer` is a JSON Pointer and is usually just "/" for a model-level validator, so it is only
 * prepended when it names something. Returns null when there is no validation array, leaving
 * `detail` as the message for every other problem type.
 *
 * Ref: ASC-7951.
 */
function problemValidationMessage(details: unknown): string | null {
  if (!isRecord(details)) return null
  const entries = details.validation
  if (!Array.isArray(entries) || entries.length === 0) return null

  const parts: string[] = []
  for (const entry of entries) {
    if (!isRecord(entry)) continue
    // `message` is the errorzen field name; `msg` tolerated for a producer that copied FastAPI.
    const raw = pickString(entry.message) ?? pickString(entry.msg)
    if (raw === null) continue
    const clean = stripValueErrorPrefix(raw)
    const pointer = pickString(entry.pointer)
    const path = pointer !== null && pointer !== '/' ? pointer.replace(/^\//, '') : ''
    parts.push(path !== '' && !clean.includes(path) ? `${path}: ${clean}` : clean)
  }
  if (parts.length === 0) return null
  if (parts.length === 1) return parts[0]
  return `Validation failed (${parts.length} issues): ${parts.join('; ')}`
}

/**
 * Flattens FastAPI's 422 `detail` array into a message a HUMAN can act on.
 *
 * This used to return `Validation failed (1 issue)` and put every real message in `details`,
 * which nothing rendered. So a connector rejected for a genuine, specific reason — e.g.
 * "incremental change capture requires fetch_mode 'keyset' — the server-side cursor runs the
 * query verbatim" — surfaced in the UI as "The request failed validation", and the operator had
 * to open devtools to learn what to change. A count is not information; the `msg` is the entire
 * value of a validation error.
 *
 * Pydantic prefixes a `model_validator` failure with "Value error, " and reports `loc: []` for
 * one, so the prefix is stripped and `loc` is only prepended when it actually names a field AND
 * the message does not already carry the path (DI's stream validators embed
 * `adapterConfig.streams[0]:` themselves, and printing it twice reads like two errors).
 *
 * Ref: ASC-7951.
 */
function validationMessage(detail: readonly unknown[]): string {
  const count = detail.length
  const parts: string[] = []
  for (const item of detail) {
    if (!isRecord(item)) continue
    const msg = pickString(item.msg)
    if (msg === null) continue
    const clean = stripValueErrorPrefix(msg)
    // FastAPI always leads with the request part ("body"/"query"/"path"); that is noise to a
    // user looking at a form.
    const loc = Array.isArray(item.loc)
      ? item.loc
          .filter((seg) => typeof seg === 'string' || typeof seg === 'number')
          .map(String)
          .filter((seg, i) => !(i === 0 && ['body', 'query', 'path', 'header'].includes(seg)))
          .join('.')
      : ''
    parts.push(loc !== '' && !clean.includes(loc) ? `${loc}: ${clean}` : clean)
  }
  if (parts.length === 0) {
    // Nothing carried a `msg` — the count is all there is, so keep the old wording rather
    // than inventing a reason.
    return `Validation failed (${count} issue${count === 1 ? '' : 's'})`
  }
  if (parts.length === 1) return parts[0]
  return `Validation failed (${parts.length} issues): ${parts.join('; ')}`
}

function pickString(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function pickBoolean(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null
}

/** Extracts code/message/requestId/traceId/retryable/details from a flat error record. */
function extractFromRecord(record: Record<string, unknown>): ExtractedShape {
  return {
    code: pickString(record.code),
    message: pickString(record.message) ?? pickString(record.error),
    requestId: pickString(record.request_id) ?? pickString(record.requestId),
    traceId: pickString(record.trace_id) ?? pickString(record.traceId),
    retryable: pickBoolean(record.retryable),
    details: 'details' in record ? record.details : null,
  }
}

/** Normalizes a parsed JSON error body into an `ExtractedShape`. Never throws. */
function extractErrorShape(parsed: unknown): ExtractedShape {
  if (!isRecord(parsed)) {
    return emptyShape()
  }

  const { detail } = parsed

  // Shape 1 — FastAPI default: { detail: "string" }
  if (typeof detail === 'string') {
    return { ...emptyShape(), message: detail }
  }

  // Shape 6 — FastAPI 422 validation error: { detail: [{ loc, msg, type }, ...] }
  if (Array.isArray(detail)) {
    return {
      ...emptyShape(),
      message: validationMessage(detail),
      // Kept verbatim regardless — callers that render per-field errors read `loc`/`type`
      // from here, and the flattened `message` is lossy by construction.
      details: detail,
    }
  }

  // Shape 2 — nested detail object: llmrouterzen / contextzen / DI style
  if (isRecord(detail)) {
    return extractFromRecord(detail)
  }

  const { error } = parsed

  // Shape 4 — insights / developer_api style: { error: "string", code?, details? }
  if (typeof error === 'string') {
    return {
      ...emptyShape(),
      message: error,
      code: pickString(parsed.code),
      details: 'details' in parsed ? parsed.details : null,
    }
  }

  // Shape 5 — future errorzen envelope: { error: { code, message, ... } }
  if (isRecord(error)) {
    const nested = extractFromRecord(error)
    return {
      ...nested,
      details: nested.details ?? ('details' in parsed ? parsed.details : null),
    }
  }

  // Shape 3 — flat: { code, message }
  if ('code' in parsed || 'message' in parsed) {
    return extractFromRecord(parsed)
  }

  return emptyShape()
}

// ── problem+json recognition (v2 — ASC-6338) ────────────────────────────────
// The canonical Scorpio wire format (RFC 9457 + errorzen extensions) is
// recognized and extracted BEFORE the legacy shapes above. Order is
// load-bearing: a problem body carries a string `detail`, so running the
// legacy path first would mis-parse it as shape 1.

/**
 * A body is problem+json when the response `Content-Type` says so, OR the
 * parsed body is a flat object carrying a `code` string plus at least one of
 * `title` / `type` / `status` (shape detection for producers that mislabel
 * the content type).
 */
function looksLikeProblemJson(response: Response, parsed: unknown): boolean {
  const contentType = response.headers.get('Content-Type') ?? ''
  if (contentType.toLowerCase().includes('application/problem+json')) {
    return true
  }
  return (
    isRecord(parsed) &&
    typeof parsed.code === 'string' &&
    ('title' in parsed || 'type' in parsed || 'status' in parsed)
  )
}

/**
 * Parses a `Retry-After` header into whole seconds. Accepts both RFC 9110
 * forms — delta-seconds (an integer; Scorpio producers are guaranteed to emit
 * this) and an HTTP-date (defensive, for external / legacy responses).
 * Returns null when absent or unparseable; HTTP-dates in the past clamp to 0.
 */
function parseRetryAfter(value: string | null): number | null {
  if (value === null) return null
  const trimmed = value.trim()
  if (trimmed.length === 0) return null
  if (/^\d+$/.test(trimmed)) return Number.parseInt(trimmed, 10)
  const dateMs = Date.parse(trimmed)
  if (Number.isNaN(dateMs)) return null
  return Math.max(0, Math.ceil((dateMs - Date.now()) / 1000))
}

/** Reads a non-2xx response body once and builds a tolerant, typed `ApiError`. */
async function buildApiError(response: Response): Promise<ApiError> {
  const { status, statusText } = response
  const legacyMessage = `HTTP ${status}: ${statusText}`

  const headerRequestId = response.headers.get('X-Request-Id')
  const headerTraceId = response.headers.get('X-Trace-Id')
  const headerRetryAfter = parseRetryAfter(response.headers.get('Retry-After'))

  let rawText = ''
  try {
    rawText = await response.text()
  } catch {
    rawText = ''
  }

  let parsed: unknown
  let parseFailed = rawText.length === 0
  if (!parseFailed) {
    try {
      parsed = JSON.parse(rawText)
    } catch {
      parseFailed = true
    }
  }

  // Shape 7 — non-JSON / empty body: fall back to the legacy HTTP string,
  // keep a short raw-text snippet in `details` for debugging.
  if (parseFailed) {
    const snippet = rawText.trim().length > 0 ? rawText.slice(0, 500) : null
    return new ApiError(
      legacyMessage,
      status,
      null,
      snippet,
      headerTraceId,
      headerRequestId,
      null,
      rawText.length > 0 ? rawText : null,
      null,
      headerRetryAfter,
      null,
    )
  }

  // problem+json branch (v2) — recognized bodies are validated against the
  // OPEN ProblemDetailsWireSchema (unknown codes and extension members
  // tolerated) and extracted BEFORE the legacy string-`detail` short-circuit.
  // A recognized body that fails validation falls through to the tolerant
  // legacy path — recognition never makes parsing stricter.
  if (looksLikeProblemJson(response, parsed)) {
    const problem = ProblemDetailsWireSchema.safeParse(parsed)
    if (problem.success) {
      const p = problem.data
      return new ApiError(
        // Mapping: a 422's `details.validation[]` first — for a validation failure `detail` is
        // the registry's generic copy ("The request failed validation.") and the actionable text
        // lives in the extension member. Then `detail`, then `title` (required on the wire) as
        // the fallback when `detail` is absent.
        problemValidationMessage(p.details) ?? p.detail ?? p.title,
        p.status,
        p.code,
        p.details ?? null,
        p.trace_id ?? headerTraceId,
        p.request_id ?? headerRequestId,
        p.retryable,
        parsed,
        p.title,
        p.retry_after_seconds ?? headerRetryAfter,
        p.type,
      )
    }
  }

  const shape = extractErrorShape(parsed)

  return new ApiError(
    shape.message ?? legacyMessage,
    status,
    shape.code,
    shape.details,
    shape.traceId ?? headerTraceId,
    shape.requestId ?? headerRequestId,
    shape.retryable,
    parsed,
    null,
    headerRetryAfter,
    null,
  )
}

/**
 * Build an `ApiError` from a problem body carried INSIDE a stream frame.
 *
 * The SSE counterpart of the problem+json branch in `buildApiError` above, and it
 * exists because that branch is unreachable on a streamed failure: the response
 * already left as 200 `text/event-stream`, so there is no non-2xx `Response` to
 * parse and `safeStream` has long since resolved successfully. The error rides a
 * frame in the body instead, and every SSE consumer had to invent its own handling
 * — which in practice meant throwing a plain `Error`, so `toErrorPresentation`
 * correctly refused to show it and every streamed failure rendered as
 * "Something needs attention". Ref: ASC-8440.
 *
 * Returns `null` when `data` is not a valid problem body, and that is the
 * load-bearing part of the contract: agents and services deploy independently, so
 * a converted agent WILL meet a server still emitting the old frame shape (and the
 * reverse). A null lets the caller keep whatever fallback it already had rather
 * than manufacturing an empty error, which is what makes each channel's conversion
 * safe to ship on its own.
 *
 * Validation is the same OPEN `ProblemDetailsWireSchema` used for HTTP, so a `code`
 * this bundle has never seen is tolerated (rule 2 in `toErrorPresentation`) while
 * an unvalidated body is still never shown verbatim (rule 3).
 */
export function apiErrorFromStreamProblem(data: unknown): ApiError | null {
  const problem = ProblemDetailsWireSchema.safeParse(data)
  if (!problem.success) return null
  const p = problem.data
  return new ApiError(
    // Same precedence as the HTTP branch, for the same reason — a 422's actionable
    // text lives in `details.validation[]`, not in the registry-generic `detail`.
    problemValidationMessage(p.details) ?? p.detail ?? p.title,
    p.status,
    p.code,
    p.details ?? null,
    // No response headers on a frame: the body is the ONLY source of correlation,
    // which is why `stream_problem` always emits `request_id`.
    p.trace_id ?? null,
    p.request_id,
    p.retryable,
    data,
    p.title,
    p.retry_after_seconds ?? null,
    p.type,
  )
}

// ── shared transport step ────────────────────────────────────────────────────

type FetchAttempt = { ok: true; response: Response } | SafeFetchError

/**
 * The transport step shared by every sanctioned client: fetch with
 * `credentials: 'include'`, a typed `ApiError` on non-2xx, and a plain
 * `Error` (no `status`) on network failure. Never throws.
 */
async function fetchChecked(url: string, init?: RequestInit): Promise<FetchAttempt> {
  try {
    const response = await fetch(url, {
      credentials: 'include',
      ...init,
    })

    if (!response.ok) {
      return {
        ok: false,
        error: await buildApiError(response),
        status: response.status,
      }
    }

    return { ok: true, response }
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err : new Error(String(err)),
    }
  }
}

// ── safeFetch ────────────────────────────────────────────────────────────────

/**
 * Fetch a URL and validate the JSON response against a Zod schema.
 *
 * - Uses `.safeParse()` internally — never throws
 * - Default `credentials: 'include'` for HTTP-only cookie auth
 * - Returns a discriminated union: `{ ok: true, data, status }` or `{ ok: false, error, status? }`
 * - On a non-2xx response, `error` is an `ApiError` — see the shape-normalization
 *   table above `extractErrorShape` for how the backend's error envelope is parsed.
 *   Zod validation failures return `FetchValidationError`; network failures
 *   (fetch itself throwing) return a plain `Error` with no `status`.
 */
export async function safeFetch<T extends z.ZodTypeAny>(
  url: string,
  schema: T,
  init?: RequestInit,
): Promise<SafeFetchResult<z.infer<T>>> {
  const attempt = await fetchChecked(url, init)
  // CAST, not narrowing, intentionally — the platform host compiles with
  // `strict: false`, where TS does not narrow the `ok` discriminant in return
  // position (same reason safeFetchOrThrow casts). Under the strict agent
  // tsconfigs the cast is a no-op. Ref: ASC-6338
  if (!attempt.ok) return attempt as SafeFetchError
  const { response } = attempt

  let json: unknown
  try {
    json = await response.json()
  } catch (err) {
    // Invalid JSON on a 2xx — a plain Error with no status, matching the
    // pre-refactor behavior (the outer try/catch used to swallow this).
    return {
      ok: false,
      error: err instanceof Error ? err : new Error(String(err)),
    }
  }

  const result = schema.safeParse(json)

  if (!result.success) {
    return {
      ok: false,
      error: new FetchValidationError(
        `Response validation failed: ${result.error.message}`,
        result.error.issues,
        response.status,
      ),
      status: response.status,
    }
  }

  return { ok: true, data: result.data, status: response.status }
}

// ── sanctioned non-JSON variants (FR-025 — ASC-6338) ─────────────────────────
// The repo has legitimate non-JSON cases, so a blanket `fetch` ban is wrong.
// These are the sanctioned clients beside `safeFetch`; each surfaces the same
// typed `ApiError` on non-2xx via the shared transport step above.

/**
 * Sanctioned client for SSE / streaming responses (zenbroker, chat token
 * streams). On 2xx returns the raw `Response` with its body unconsumed so the
 * caller can read the stream; on non-2xx the body is consumed into a typed
 * `ApiError`.
 */
export async function safeStream(
  url: string,
  init?: RequestInit,
): Promise<SafeFetchResult<Response>> {
  const attempt = await fetchChecked(url, init)
  if (!attempt.ok) return attempt as SafeFetchError // strict:false-safe cast — see safeFetch
  return { ok: true, data: attempt.response, status: attempt.response.status }
}

/**
 * Sanctioned client for binary / blob downloads (CSV, files). On 2xx resolves
 * the body as a `Blob`; on non-2xx the body is parsed into a typed `ApiError`.
 */
export async function safeDownload(
  url: string,
  init?: RequestInit,
): Promise<SafeFetchResult<Blob>> {
  const attempt = await fetchChecked(url, init)
  if (!attempt.ok) return attempt as SafeFetchError // strict:false-safe cast — see safeFetch
  const { response } = attempt

  try {
    const blob = await response.blob()
    return { ok: true, data: blob, status: response.status }
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err : new Error(String(err)),
    }
  }
}

/**
 * Sanctioned client for 204 No Content mutations (and any call whose response
 * body is irrelevant — the body is never read on success). `data` is always
 * `null`; on non-2xx the body is parsed into a typed `ApiError`.
 */
export async function safeFetchNoContent(
  url: string,
  init?: RequestInit,
): Promise<SafeFetchResult<null>> {
  const attempt = await fetchChecked(url, init)
  if (!attempt.ok) return attempt as SafeFetchError // strict:false-safe cast — see safeFetch
  return { ok: true, data: null, status: attempt.response.status }
}

// ── safeFetchOrThrow ─────────────────────────────────────────────────────────

/**
 * Like `safeFetch`, but throws on failure instead of returning an error result.
 * Use sparingly — prefer `safeFetch` for explicit error handling.
 */
export async function safeFetchOrThrow<T extends z.ZodTypeAny>(
  url: string,
  schema: T,
  init?: RequestInit,
): Promise<z.infer<T>> {
  const result = await safeFetch(url, schema, init)
  if (result.ok) return result.data
  throw (result as SafeFetchError).error
}
